import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { ensureProfile } from '@/utils/ensureProfile';

type Profile = {
  id: string;
  email: string | null;
  phone: string | null;
  role: 'user' | 'admin';
  balance: number;
  kyc_status: 'pending' | 'approved' | 'rejected';
  currency: string;
};

type AuthContextType = {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  error?: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (sessionError) {
          console.error('Session error:', sessionError);
          setError(sessionError.message);
          setLoading(false);
          return;
        }

        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id, isMounted);
        } else {
          setLoading(false);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Auth initialization error:', err);
        setError(err instanceof Error ? err.message : 'Authentication initialization failed');
        setLoading(false);
      }
    };
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!isMounted) return;

        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id, isMounted);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId: string, isMounted: boolean) => {
    try {
      console.log('Fetching profile for user:', userId);

      // First, ensure the profile exists
      try {
        await ensureProfile(userId);
      } catch (ensureError) {
        console.error('Warning: Could not ensure profile:', ensureError);
        // Don't fail here - continue trying to fetch
      }

      // Now fetch it
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, phone, role, balance, kyc_status, currency')
        .eq('id', userId)
        .single();

      if (!isMounted) return;

      if (error) {
        console.error('Profile fetch error:', error);

        // If profile fetch fails due to RLS or permissions, create a default profile
        if (error.code === 'PGRST116' || error.message?.includes('permission')) {
          console.log('Creating default local profile...');
          const defaultProfile = {
            id: userId,
            email: null,
            phone: null,
            role: 'user' as const,
            balance: 0,
            kyc_status: 'pending' as const,
            currency: 'USD'
          };
          setProfile(defaultProfile);
          setLoading(false);
          return;
        }
        setError(error.message);
        setLoading(false);
        return;
      }
      console.log('Profile loaded:', data);
      setProfile(data);
      setLoading(false);
    } catch (error) {
      if (!isMounted) return;
      console.error('Error in fetchProfile:', error);
      // Don't set error - allow user to proceed with default profile
      setLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signOut, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}